import * as Sentry from '@sentry/react';

import {ENV} from '@/constants/EnvConstants';
import {isLocalhost, isProduction} from '@/utils/environment';
import {getPackageVersion} from '@/utils/getPackageVersion';

export const initSentry = (): void => {
  const notTestingEnv = isProduction;
  const isSentryInitialized = !!Sentry.getCurrentHub().getClient();
  const isLocalHost = isLocalhost;
  if (!isLocalHost && !isSentryInitialized) {
    Sentry.init({
      dsn: ENV.VITE_SENTRY_DNS,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      environment: ENV.VITE_APP_ENV,
      tracesSampleRate: notTestingEnv ? 1.0 : 0.2,
      _experiments: {
        profilesSampleRate: notTestingEnv ? 1.0 : 0.2,
      },
      beforeSend: event => transformEventSentry(event),
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
      normalizeDepth: 10,
      // release + dist must match vite.config sentryVitePlugin so Sentry can show stack-trace source context.
      // Source maps are only uploaded on `vite build` (not in `yarn dev`). To see context: use a built app (e.g. yarn build:dev:preview or deploy).
      release: getPackageVersion().replace(/^"|"$/g, ''),
      dist: `${ENV.VITE_APP_ENV}-${getPackageVersion().replace(/^"|"$/g, '')}`,
    });
  }
};

/**
 * Returns true if this is the known Facebook in-app browser (FB_IAB) bug:
 * TypeError "Cannot read properties of undefined (reading 'value')" inside
 * setContactAutofillValuesFromBridge when the user focuses an input (e.g. phone
 * or address). FB IAB injects autofill code that assumes DOM elements have
 * .value; our masked/controlled inputs can break that. We cannot fix third-party
 * FB IAB code; filter to avoid Sentry noise.
 */
function isFacebookIabAutofillBug(event: Sentry.ErrorEvent): boolean {
  const firstException = event.exception?.values?.[0];
  const stack = firstException?.stacktrace?.frames ?? [];
  const hasBridgeFrame = stack.some(frame =>
    frame.function?.includes('setContactAutofillValuesFromBridge'),
  );
  if (!hasBridgeFrame) return false;

  const type = firstException?.type ?? '';
  const message = firstException?.value ?? event.message ?? '';
  const isRelevantType = type === 'TypeError';
  const isRelevantMessage =
    typeof message === 'string' &&
    (message.includes("reading 'value'") ||
      message.includes('reading "value"'));
  return isRelevantType && isRelevantMessage;
}

const transformEventSentry = (
  event: Sentry.ErrorEvent,
): Sentry.ErrorEvent | null => {
  if (isFacebookIabAutofillBug(event)) {
    return null;
  }
  return event;
};
